import { Button, Chip, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { CTM_Colors, SessionGridRow } from '../Helper';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface TestingSessionDataGridProps {
  rows: SessionGridRow[];
  onEdit: (session: SessionGridRow) => void;
  onDelete: (sessionId: string, sessionName: string) => void;
}

const TestingSessionDataGrid: React.FC<TestingSessionDataGridProps> = ({ rows, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const columns: GridColDef<(SessionGridRow[])[number]>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
    },
    {
      field: 'info',
      headerName: 'info',
      flex: 2,
    },
    {
      field: 'date',
      headerName: 'Date',
      type: 'date',
      flex: 2,
    },
    {
      field: 'runCount',
      headerName: 'Runs',
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ?? 0}
          size="small"
          sx={{
            backgroundColor: params.value > 0 ? `${CTM_Colors.ctm_green}30` : 'transparent',
            color: params.value > 0 ? CTM_Colors.ctm_green : 'text.disabled',
            fontWeight: params.value > 0 ? 700 : 400,
            border: `1px solid ${params.value > 0 ? CTM_Colors.ctm_green : 'transparent'}`,
          }}
        />
      ),
    },
    {
      field: 'viewSession',
      headerName: 'View',
      sortable: false,
      flex: 1,
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="contained"
          sx={{ alignSelf: 'center' }}
          onClick={() => {handleViewClick(params.row)}}
        >
          View
        </Button>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 1,
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onEdit(params.row)}
            sx={{ color: CTM_Colors.ctm_green }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(params.row.id, params.row.name)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleViewClick = (row: SessionGridRow) => {
    navigate('session/' + row.id);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        sx={{
          // hide pagination because pagination={false} only works in pro version
          '& .MuiDataGrid-footerContainer': {
            display: 'none',
          },
          // to make header row bold and green
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: `${CTM_Colors.ctm_green}40`,
          },
          // title of the header rows bold
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
        }}
      />
    </Box>
  );
}

export default TestingSessionDataGrid;